import React, { useState, useEffect, useMemo } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useHistory, useLocation } from "react-router-dom";
import { ScrollNavbar, SearchSection } from "../../components";
import searchService, {
  City,
  Price,
  SearchRequest,
} from "../../services/searchService";
import CarDetailsCard from "../../components/CarDetailsCard/CarDetailsCard";
import Swal from "sweetalert2";
import { Sidebar } from "../../components";
import { FilterOption } from "../../components/Sidebar/Sidebar";
import {
  VerticalType,
  TripType,
  VERTICALS,
  TRIP_TYPES,
  getHourlyDurationKey,
  isSourceFieldVisible,
  isDestinationFieldVisible,
  isReturnLegRequired,
} from "../../constants/tripTypes";
import {
  DrivingDistanceResult,
  getDrivingDistance,
} from "../../utils/distanceHelper";
import { PlaceSelection } from "../../components/PlaceAutocompleteInput/PlaceAutocompleteInput";
import { findNearestCity } from "../../utils/geo";
import { Stop } from "../../types/stop";
import { FareLeg } from "../../utils/fareCalculator";

const Dashboard = () => {
  const { user } = useAuth();
  const history = useHistory();
  const location = useLocation();

  // Form state
  const [activeTab, setActiveTab] = useState<VerticalType>(VERTICALS.INTERCITY);
  const [tripType, setTripType] = useState<TripType>(TRIP_TYPES.ONEWAY);
  const [source, setSource] = useState<City | null>(null);
  const [destination, setDestination] = useState<City | null>(null);
  const [departureDate, setDepartureDate] = useState<string>("");
  const [returnDate, setReturnDate] = useState<string>("");
  const [pickupTime, setPickupTime] = useState<string>("");
  const [dropTime, setDropTime] = useState<string>("");
  const [sourceSearch, setSourceSearch] = useState<string>("");
  const [destSearch, setDestSearch] = useState<string>("");
  const [distanceKm, setDistanceKm] = useState<number | undefined>(undefined);

  // Exact pickup/drop address captured via Google Places Autocomplete - used
  // for the precise distance calc and the booking's pickup/drop address text.
  // `source`/`destination` (City) is still derived from these (nearest match)
  // since Price/Plan routes are configured per city pair, not exact address.
  const [sourceAddress, setSourceAddress] = useState<PlaceSelection | null>(
    null
  );
  const [destinationAddress, setDestinationAddress] =
    useState<PlaceSelection | null>(null);
  const [allCities, setAllCities] = useState<City[]>([]);

  // Multi-City itinerary - mid-stops between source and destination, each
  // with an estimated date/time used only to decide which legs count as
  // night legs for pricing (see fareCalculator's calculateMultiCityFare).
  // `fareLegs` is the resolved per-leg distance/time breakdown (forward legs
  // + the return leg back to source) computed in handleSearch - undefined
  // for every trip type except Multi-City.
  const [stops, setStops] = useState<Stop[]>([]);
  const [fareLegs, setFareLegs] = useState<FareLeg[] | undefined>(undefined);

  const addStop = () => {
    setStops((prev) => [
      ...prev,
      { id: crypto.randomUUID(), addressSearch: "", address: null, date: "", time: "" },
    ]);
  };

  const updateStop = (id: string, patch: Partial<Stop>) => {
    setStops((prev) => prev.map((s) => (s.id === id ? { ...s, ...patch } : s)));
  };

  const removeStop = (id: string) => {
    setStops((prev) => prev.filter((s) => s.id !== id));
  };

  // Stops only apply to Intercity Multi-City - drop them (and any stale
  // multi-leg fare breakdown) when the user navigates away from it.
  useEffect(() => {
    if (!(activeTab === VERTICALS.INTERCITY && tripType === TRIP_TYPES.MULTICITY)) {
      setStops([]);
      setFareLegs(undefined);
    }
  }, [activeTab, tripType]);

  useEffect(() => {
    searchService
      .getAllCities()
      .then((response) => {
        if (response.success && response.data) setAllCities(response.data);
      })
      .catch((error) => console.error("Failed to load cities list:", error));
  }, []);

  const handleSourcePlaceSelected = (place: PlaceSelection) => {
    setSourceSearch(place.address);
    setSourceAddress(place);
    setSource(findNearestCity(allCities, place.lat, place.lng));
  };

  const handleDestinationPlaceSelected = (place: PlaceSelection) => {
    setDestSearch(place.address);
    setDestinationAddress(place);
    setDestination(findNearestCity(allCities, place.lat, place.lng));
  };

  // Airport-side city pick (no Google Places involved - see
  // isSourceCityOnly/isDestinationCityOnly) - the City's own lat/long is
  // used as-is, so no address override.
  const handleSourceCitySelected = (city: City) => {
    setSourceSearch(city.name);
    setSourceAddress(null);
    setSource(city);
  };

  const handleDestinationCitySelected = (city: City) => {
    setDestSearch(city.name);
    setDestinationAddress(null);
    setDestination(city);
  };

  const handleSwapLocations = () => {
    const tempSource = source;
    const tempSourceSearch = sourceSearch;
    const tempSourceAddress = sourceAddress;

    setSource(destination);
    setSourceSearch(destSearch);
    setSourceAddress(destinationAddress);

    setDestination(tempSource);
    setDestSearch(tempSourceSearch);
    setDestinationAddress(tempSourceAddress);
  };

  const handleDistanceComputed = (result: DrivingDistanceResult | null) => {
    setDistanceKm(result ? result.distanceMeters / 1000 : undefined);
  };

  // Search state
  const [searchResults, setSearchResults] = useState<Price[]>([]);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [searchAttempted, setSearchAttempted] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [initialSearchDone, setInitialSearchDone] = useState<boolean>(false);

  // Sidebar filters - applied purely client-side against searchResults,
  // never sent to the search API.
  const [filters, setFilters] = useState<{
    cabType: string[];
    fuelType: string[];
    cabModel: string[];
  }>({ cabType: [], fuelType: [], cabModel: [] });

  const handleFilterChange = <T extends string>(
    filterType: T,
    value: string,
    isChecked: boolean
  ) => {
    setFilters((prev) => {
      const key = filterType as keyof typeof prev;
      const current = prev[key];
      return {
        ...prev,
        [key]: isChecked
          ? [...current, value]
          : current.filter((v) => v !== value),
      };
    });
  };

  // Cab Model options are derived from whatever the search actually
  // returned - vehicle_model is open-ended on the backend, so a hardcoded
  // option list would silently go stale/mismatch. A "any" vehicle_model (a
  // real enum value - see fusion_backend's Price model) means the price
  // isn't tied to one specific model, so it's not a meaningful checkbox
  // choice and is left out of the list (it's still always shown - see the
  // wildcard check in filteredResults below).
  const cabModelOptions: FilterOption[] = useMemo(() => {
    const seen = new Map<string, FilterOption>();
    for (const car of searchResults) {
      if (car.vehicle_model?.toLowerCase() === "any") continue;
      const id = `${car.vehicle_manufacturer}::${car.vehicle_model}`;
      if (!seen.has(id)) {
        seen.set(id, {
          id,
          label: `${car.vehicle_manufacturer} ${car.vehicle_model}`,
        });
      }
    }
    return Array.from(seen.values());
  }, [searchResults]);

  const filteredResults: Price[] = useMemo(() => {
    return searchResults.filter((car) => {
      // Plan.vehicle_category has no "any" wildcard on the backend (it's a
      // required enum array), so this is always a literal comparison.
      const cabTypeMatch =
        filters.cabType.length === 0 ||
        (car.Plan.vehicle_category ?? []).some((c) =>
          filters.cabType.includes(c)
        );

      // fuel_type "any" (a real enum value) means the price works with any
      // fuel, so it should pass every fuel filter rather than none of them.
      const fuelTypeMatch =
        filters.fuelType.length === 0 ||
        car.fuel_type?.toLowerCase() === "any" ||
        filters.fuelType.some(
          (f) => f.toLowerCase() === car.fuel_type?.toLowerCase()
        );

      // Same "any" wildcard for vehicle_model.
      const cabModelId = `${car.vehicle_manufacturer}::${car.vehicle_model}`;
      const cabModelMatch =
        filters.cabModel.length === 0 ||
        car.vehicle_model?.toLowerCase() === "any" ||
        filters.cabModel.includes(cabModelId);

      return cabTypeMatch && fuelTypeMatch && cabModelMatch;
    });
  }, [searchResults, filters]);

  // Determine if search is enabled
  const isSearchEnabled = (() => {
    if (!departureDate || !pickupTime) return false;
    if (isSourceFieldVisible(activeTab, tripType) && !source) return false;
    if (isDestinationFieldVisible(activeTab, tripType) && !destination) return false;
    if (isReturnLegRequired(tripType) && (!returnDate || !dropTime)) return false;
    if (
      activeTab === VERTICALS.INTERCITY &&
      tripType === TRIP_TYPES.MULTICITY &&
      stops.some((s) => !s.address || !s.date || !s.time)
    )
      return false;
    return true;
  })();

  // Load data from URL parameters - only once on initial load
  useEffect(() => {
    const loadFromUrl = async () => {
      setIsLoading(true);

      try {
        // Parse URL parameters
        const queryParams = new URLSearchParams(location.search);

        // Process vertical + trip type (same param scheme Landing.tsx emits)
        const verticalParam = queryParams.get("vertical");
        const parsedVertical: VerticalType = (
          Object.values(VERTICALS) as string[]
        ).includes(verticalParam || "")
          ? (verticalParam as VerticalType)
          : VERTICALS.INTERCITY;

        const tripTypeParam = queryParams.get("trip-type");
        const parsedTripType: TripType = (
          Object.values(TRIP_TYPES) as string[]
        ).includes(tripTypeParam || "")
          ? (tripTypeParam as TripType)
          : TRIP_TYPES.ONEWAY;

        setActiveTab(parsedVertical);
        setTripType(parsedTripType);

        // Process simple parameters
        const departureDateParam = queryParams.get("departure-date") || "";
        const returnDateParam = queryParams.get("return-date") || "";
        const pickupTimeParam = queryParams.get("pickup-time") || "";
        const dropTimeParam = queryParams.get("drop-time") || "";

        setDepartureDate(departureDateParam);
        setReturnDate(returnDateParam);
        setPickupTime(pickupTimeParam);
        setDropTime(dropTimeParam);

        // Process city parameters
        const sourceParam = queryParams.get("source");
        const destParam = queryParams.get("destination");

        // Initialize city data
        let sourceCityData: City | null = null;
        let destCityData: City | null = null;

        // Fetch cities if parameters exist
        if (sourceParam) {
          try {
            const sourceCities = await searchService.searchCities(
              sourceParam,
              5
            );
            if (sourceCities && sourceCities.length > 0) {
              sourceCityData = sourceCities[0];
              setSource(sourceCityData);
              setSourceSearch(sourceCityData.name);
            }
          } catch (error) {
            console.error("Error fetching source city:", error);
          }
        }

        if (destParam) {
          try {
            const destCities = await searchService.searchCities(destParam, 5);
            if (destCities && destCities.length > 0) {
              destCityData = destCities[0];
              setDestination(destCityData);
              setDestSearch(destCityData.name);
            }
          } catch (error) {
            console.error("Error fetching destination city:", error);
          }
        }

        // Perform initial search if we have all required parameters
        const canSearch =
          (sourceParam || !isSourceFieldVisible(parsedVertical, parsedTripType)) &&
          (destParam || !isDestinationFieldVisible(parsedVertical, parsedTripType)) &&
          departureDateParam &&
          pickupTimeParam &&
          (!isReturnLegRequired(parsedTripType) ||
            (returnDateParam && dropTimeParam));

        if (canSearch) {
          const destinationId =
            parsedVertical === VERTICALS.HOURLY
              ? sourceCityData?.id
              : destCityData?.id;

          if (sourceCityData && destinationId) {
            // Airport Plans always store the airport-side City in `source`
            // and leave `destination` null, regardless of trip_type (see the
            // matching comment in handleSearch below) - the city resolved
            // into the source field for Pickup / destination field for
            // Drop-off always goes into the search request's `source`.
            const searchData: SearchRequest = {
              trip_type: parsedTripType,
              source:
                parsedVertical === VERTICALS.AIRPORT &&
                parsedTripType === TRIP_TYPES.DROPOFF
                  ? destCityData!.id
                  : sourceCityData.id,
              destination:
                parsedVertical === VERTICALS.AIRPORT ? null : destinationId,
            };

            if (parsedVertical === VERTICALS.HOURLY) {
              searchData.hours = getHourlyDurationKey(parsedTripType) ?? "4";
            } else if (destCityData) {
              // Live Google Maps route distance, computed here (not left to
              // the preview map's own effect) so it's ready before results
              // render.
              const distanceResult = await getDrivingDistance(
                { lat: sourceCityData.latitude, lng: sourceCityData.longitude },
                { lat: destCityData.latitude, lng: destCityData.longitude }
              );
              setDistanceKm(
                distanceResult ? distanceResult.distanceMeters / 1000 : undefined
              );
            }

            performSearch(searchData);
            setInitialSearchDone(true);
          }
        }
      } catch (error) {
        console.error("Error loading from URL:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadFromUrl();
    // This effect should only run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Perform the actual search
  const performSearch = (searchData: SearchRequest) => {
    setIsSearching(true);
    setSearchResults([]);
    // Cab Model options are result-specific - drop any stale selection from
    // a previous search's result set.
    setFilters((prev) => ({ ...prev, cabModel: [] }));

    // Perform the search
    searchService
      .searchPrices(searchData)
      .then((response) => {
        if (response.success && response.data && response.data.length > 0) {
          setSearchResults(response.data);
        } else {
          setSearchResults([]);
        }
      })
      .catch((error) => {
        console.error("Search failed:", error);
        Swal.fire({
          icon: "error",
          title: "Search Error",
          text: "An unexpected error occurred while searching for cabs. Please try again.",
        });
      })
      .finally(() => {
        setIsSearching(false);
      });
  };

  // Handle search
  const handleSearch = async () => {
    setSearchAttempted(true);

    const sourceRequired = isSourceFieldVisible(activeTab, tripType);
    const destinationRequired = isDestinationFieldVisible(activeTab, tripType);
    const returnLegRequired = isReturnLegRequired(tripType);

    if ((!source && sourceRequired) || (!destination && destinationRequired)) {
      Swal.fire({
        icon: "error",
        title: "Missing Information",
        text:
          "Please select " +
          [sourceRequired && "source", destinationRequired && "destination"]
            .filter(Boolean)
            .join(" and ") +
          ".",
      });
      return;
    }

    if (!departureDate) {
      Swal.fire({
        icon: "error",
        title: "Missing Information",
        text: "Please select a departure date.",
      });
      return;
    }

    if (returnLegRequired && !returnDate) {
      Swal.fire({
        icon: "error",
        title: "Missing Information",
        text: "Please select a return date for your round trip.",
      });
      return;
    }

    if (!pickupTime) {
      Swal.fire({
        icon: "error",
        title: "Missing Information",
        text: "Please select a pickup time.",
      });
      return;
    }

    if (returnLegRequired && !dropTime) {
      Swal.fire({
        icon: "error",
        title: "Missing Information",
        text: "Please select a drop time for your round trip.",
      });
      return;
    }

    const isMultiCity =
      activeTab === VERTICALS.INTERCITY && tripType === TRIP_TYPES.MULTICITY;

    if (isMultiCity) {
      const incompleteIndex = stops.findIndex(
        (s) => !s.address || !s.date || !s.time
      );
      if (incompleteIndex !== -1) {
        Swal.fire({
          icon: "error",
          title: "Missing Information",
          text: `Please complete Stop ${incompleteIndex + 1} (address, date, and time).`,
        });
        return;
      }
    }

    // Airport Plans always store the airport-side City in `source` and
    // leave `destination` null, regardless of whether trip_type is pickup
    // or drop-off - confirmed against fusion_admindash's Plan form, which
    // clears/disables `destination` for vertical==="airport" unconditionally
    // (not gated on trip_type). So the city the customer picked - source
    // field for Pickup, destination field for Drop-off (see
    // isSourceCityOnly/isDestinationCityOnly) - always goes into the search
    // request's `source`, never `destination`.
    const searchData: SearchRequest =
      activeTab === VERTICALS.AIRPORT
        ? {
            trip_type: tripType,
            source: (tripType === TRIP_TYPES.PICKUP ? source : destination)!
              .id,
            destination: null,
          }
        : {
            trip_type: tripType,
            source: source!.id,
            destination:
              activeTab !== VERTICALS.HOURLY ? destination!.id : source!.id,
          };

    if (activeTab === VERTICALS.HOURLY) {
      searchData.hours = getHourlyDurationKey(tripType) ?? "4";
      setFareLegs(undefined);
    } else if (isMultiCity) {
      // Multi-City: forward route through every stop (one Routes API call,
      // using `intermediates` for the per-leg breakdown) plus a separate
      // call for the return leg back to source - that return leg is a pure
      // pricing input (see fareCalculator's calculateMultiCityFare), never
      // rendered as a route/itinerary step anywhere.
      const originLatLng = {
        lat: sourceAddress?.lat ?? source!.latitude,
        lng: sourceAddress?.lng ?? source!.longitude,
      };
      const destLatLng = {
        lat: destinationAddress?.lat ?? destination!.latitude,
        lng: destinationAddress?.lng ?? destination!.longitude,
      };
      const waypoints = stops.map((s) => ({
        lat: s.address!.lat,
        lng: s.address!.lng,
      }));

      const [forward, back] = await Promise.all([
        getDrivingDistance(originLatLng, destLatLng, waypoints),
        getDrivingDistance(destLatLng, originLatLng),
      ]);

      if (!forward || !back) {
        setDistanceKm(undefined);
        setFareLegs(undefined);
      } else {
        const forwardLegs =
          forward.legs && forward.legs.length > 0
            ? forward.legs
            : [{ distanceMeters: forward.distanceMeters, durationSeconds: forward.durationSeconds }];
        // Leg i departs at the time of whichever stop/pickup starts it:
        // leg 0 = pickupTime, leg i (i>0) = stops[i-1].time.
        const legTimes = [pickupTime, ...stops.map((s) => s.time)];
        const legs: FareLeg[] = forwardLegs.map((leg, i) => ({
          distanceKm: leg.distanceMeters / 1000,
          time: legTimes[i] ?? pickupTime,
        }));
        legs.push({ distanceKm: back.distanceMeters / 1000, time: dropTime || pickupTime });

        setDistanceKm(legs.reduce((sum, leg) => sum + leg.distanceKm, 0));
        setFareLegs(legs);
      }
    } else {
      setFareLegs(undefined);
      // Live Google Maps route distance for the resolved source/destination
      // pair - computed here, synchronously as part of the search. Prefers
      // the exact captured address (the free/non-airport side of an Airport
      // search), falling back to the City's own lat/long - which for the
      // Airport-side City-picker field is exactly the airport's
      // coordinates, per how Plans are configured for that vertical.
      const originLat = sourceAddress?.lat ?? source?.latitude;
      const originLng = sourceAddress?.lng ?? source?.longitude;
      const destLat = destinationAddress?.lat ?? destination?.latitude;
      const destLng = destinationAddress?.lng ?? destination?.longitude;

      if (originLat != null && originLng != null && destLat != null && destLng != null) {
        const distanceResult = await getDrivingDistance(
          { lat: originLat, lng: originLng },
          { lat: destLat, lng: destLng }
        );
        setDistanceKm(
          distanceResult ? distanceResult.distanceMeters / 1000 : undefined
        );
      } else {
        setDistanceKm(undefined);
      }
    }

    // Update URL with current search parameters (without triggering a reload)
    const params = new URLSearchParams();
    params.set("vertical", activeTab);
    params.set("trip-type", tripType);
    if (source) params.set("source", source.name);
    if (destination) params.set("destination", destination.name);
    if (departureDate) params.set("departure-date", departureDate);
    if (returnDate) params.set("return-date", returnDate);
    if (pickupTime) params.set("pickup-time", pickupTime);
    if (dropTime) params.set("drop-time", dropTime);
    if (activeTab === VERTICALS.HOURLY) {
      params.set("hourly-duration", getHourlyDurationKey(tripType) ?? "4");
    }

    history.replace({
      pathname: location.pathname,
      search: params.toString(),
    });

    // Perform the search
    performSearch(searchData);
  };

  // Handle clear filter
  const handleClearFilter = () => {
    // Clear all form state
    setSource(null);
    setDestination(null);
    setSourceSearch("");
    setDestSearch("");
    setSourceAddress(null);
    setDestinationAddress(null);
    setDepartureDate("");
    setReturnDate("");
    setPickupTime("");
    setDropTime("");
    setDistanceKm(undefined);
    setStops([]);
    setFareLegs(undefined);
    setSearchResults([]);
    setSearchAttempted(false);
    setFilters({ cabType: [], fuelType: [], cabModel: [] });

    // Clear URL parameters
    history.replace({
      pathname: location.pathname,
      search: "",
    });
  };

  // Determine if clear button should be shown
  const showClearButton = !!(
    source ||
    destination ||
    departureDate ||
    returnDate ||
    pickupTime ||
    dropTime ||
    sourceSearch ||
    destSearch ||
    searchResults.length > 0
  );

  return (
    <div className="min-h-screen bg-[#F3F4F6]">
      <ScrollNavbar />
      <div className="w-full flex">
      <div className="w-1/4">
        <Sidebar
          onFilterChange={handleFilterChange}
          initialFilters={filters}
          cabModelOptions={cabModelOptions}
        />
      </div>
      <div className="max-w-[1400px] mx-auto py-3 px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-lg overflow-visible">
          {isLoading ? (
            <div className="p-6 flex justify-center items-center min-h-[200px]">
              <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-600"></div>
              <p className="ml-3 text-gray-500">Loading search form...</p>
            </div>
          ) : (
            <SearchSection
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              tripType={tripType}
              setTripType={setTripType}
              source={source}
              setSource={setSource}
              destination={destination}
              setDestination={setDestination}
              departureDate={departureDate}
              setDepartureDate={setDepartureDate}
              returnDate={returnDate}
              setReturnDate={setReturnDate}
              pickupTime={pickupTime}
              setPickupTime={setPickupTime}
              dropTime={dropTime}
              setDropTime={setDropTime}
              sourceSearch={sourceSearch}
              setSourceSearch={setSourceSearch}
              destSearch={destSearch}
              setDestSearch={setDestSearch}
              sourceAddress={sourceAddress}
              destinationAddress={destinationAddress}
              onSourcePlaceSelected={handleSourcePlaceSelected}
              onDestinationPlaceSelected={handleDestinationPlaceSelected}
              allCities={allCities}
              onSourceCitySelected={handleSourceCitySelected}
              onDestinationCitySelected={handleDestinationCitySelected}
              stops={stops}
              onAddStop={addStop}
              onUpdateStop={updateStop}
              onRemoveStop={removeStop}
              onSwapLocations={handleSwapLocations}
              handleSearch={handleSearch}
              handleClearFilter={handleClearFilter}
              isSearchEnabled={isSearchEnabled}
              showClearButton={showClearButton}
              animationEnabled={false}
              onDistanceComputed={handleDistanceComputed}
            />
          )}

          {isSearching && (
            <div className="p-6 border-t">
              <div className="flex justify-center">
                <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-600"></div>
              </div>
              <p className="text-center mt-4 text-gray-500">
                Searching for available cabs...
              </p>
            </div>
          )}

          {!isSearching && searchResults.length > 0 && filteredResults.length > 0 && (
            <div className="p-6 border-t">
              <h2 className="text-lg font-semibold mb-4">
                Available Cabs ({filteredResults.length})
              </h2>
              <div className="space-y-4">
                {filteredResults.map((car) => (
                  <CarDetailsCard
                    key={car.id}
                    price={car}
                    distanceKm={distanceKm}
                    fareLegs={fareLegs}
                    pickupTime={pickupTime}
                    sourceState={source?.state}
                    onSelect={(id) => {
                      const returnLegRequired = isReturnLegRequired(tripType);
                      const searchParams = {
                        vertical: activeTab,
                        sourceCity: source?.name,
                        destinationCity: destination?.name,
                        // Real City UUIDs - needed because price.Plan.source/
                        // destination is null for whichever side an Airport
                        // Plan doesn't price against, so CabDetails falls
                        // back to these for the booking's source/destination.
                        sourceCityId: source?.id,
                        destinationCityId: destination?.id,
                        // Exact Places address, when captured - falls back to
                        // the resolved city name for the Airport-side City
                        // pick (that side is never a free-typed address).
                        sourceAddress: sourceAddress?.address ?? source?.name,
                        destinationAddress:
                          destinationAddress?.address ?? destination?.name,
                        sourceState: source?.state,
                        departureDate,
                        pickupTime,
                        returnDate: returnLegRequired ? returnDate : undefined,
                        dropTime: returnLegRequired ? dropTime : undefined,
                        tripType,
                        distanceKm,
                        fareLegs,
                        // Plain-object stops for the itinerary review + the
                        // booking payload's `stops` JSON field - source/
                        // destination on the booking stay the real trip
                        // endpoints, unaffected by this.
                        stops: stops.map((s) => ({
                          address: s.address?.address ?? s.addressSearch,
                          lat: s.address?.lat,
                          lng: s.address?.lng,
                          date: s.date,
                          time: s.time,
                        })),
                      };
                      history.push(`/cab-details/${id}`, { searchParams });
                    }}
                    isAvailable={true}
                  />
                ))}
              </div>
            </div>
          )}

          {!isSearching && searchResults.length === 0 && searchAttempted && (
            <div className="p-6 border-t text-center flex items-center justify-center min-h-[300px]">
              <div className="max-w-md mx-auto">
                <svg
                  className="mx-auto h-12 w-12 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    vectorEffect="non-scaling-stroke"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h14a2 2 0 012 2v10a2 2 0 01-2 2H4a2 2 0 01-2-2z"
                  />
                </svg>
                <h3 className="mt-2 text-lg font-medium text-gray-900">
                  No Cabs Found
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  We couldn't find any available cabs for the selected route and
                  time. Please try adjusting your search criteria.
                </p>
              </div>
            </div>
          )}

          {!isSearching && searchResults.length > 0 && filteredResults.length === 0 && (
            <div className="p-6 border-t text-center flex items-center justify-center min-h-[300px]">
              <div className="max-w-md mx-auto">
                <h3 className="mt-2 text-lg font-medium text-gray-900">
                  No Cabs Match Your Filters
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  {searchResults.length} cab{searchResults.length === 1 ? "" : "s"}{" "}
                  available for this route, but none match the selected filters.
                  Try clearing some filters in the sidebar.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
      </div>
    </div>
  );
};

export default Dashboard;
